/**
 * RuleRangeChoice — a rule's "measured over" window, inherited or pinned:
 * unset reads as the inherited window (dashed, "· inherited" — click to pin a
 * different one); set is the shared date-range dropdown and a clear (×) back
 * to the inherited one. Presentational: the Flow Optimizer's rules (the
 * first formula group's window) and the Automation's (the formula's first
 * range) both use it, so the two read the same.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { CalendarDays, CalendarOff, X } from 'lucide-react';

import FormulaGroupDateRangeDropdown from '../../../calculation/group/date-range/FormulaGroupDateRangeDropdown';

export interface RangeValue {
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
}

export interface RuleRangeChoiceProps {
  /** null = the rule inherits its window. */
  pinned: RangeValue | null;
  inheritedLabel: string;
  inheritedTitle: string;
  /** Limit the dropdown's presets (the Automation: whole days only). */
  presets?: ReadonlyArray<string>;
  onPin: () => void;
  onCommit: (next: RangeValue) => void;
  onClear: () => void;
}

export const RuleRangeChoice: FC<RuleRangeChoiceProps> = ({
  pinned,
  inheritedLabel,
  inheritedTitle,
  presets,
  onPin,
  onCommit,
  onClear,
}) =>
  pinned === null ? (
    <button
      type="button"
      onClick={onPin}
      title={inheritedTitle}
      className="inline-flex h-6 items-center gap-1 rounded border border-dashed border-border bg-background/40 px-2 text-[10.5px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
    >
      <CalendarDays className="h-3 w-3" aria-hidden />
      {inheritedLabel}
      <span className="text-muted-foreground/70">· inherited</span>
    </button>
  ) : (
    <span className="inline-flex items-center gap-1">
      <FormulaGroupDateRangeDropdown
        dateRange={pinned.dateRange}
        dateRangeCustomFrom={pinned.dateRangeCustomFrom}
        dateRangeCustomTo={pinned.dateRangeCustomTo}
        presets={presets}
        onCommit={(patch) =>
          onCommit({
            dateRange: patch.dateRange,
            dateRangeCustomFrom: patch.dateRange === 'custom' ? patch.dateRangeCustomFrom : undefined,
            dateRangeCustomTo: patch.dateRange === 'custom' ? patch.dateRangeCustomTo : undefined,
          })
        }
      />
      <button
        type="button"
        aria-label="Clear date range"
        title="Back to the inherited range"
        onClick={onClear}
        className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );

/** A rule made only of conditions read live (score ranking, flow tags) needs no window — say so. */
export const NoWindowNote: FC<{ text: string; title: string }> = ({ text, title }) => (
  <span
    title={title}
    className="inline-flex h-6 items-center gap-1 rounded border border-dashed border-border/60 bg-muted/20 px-2 text-[10.5px] font-medium text-muted-foreground/70"
  >
    <CalendarOff className="h-3 w-3 opacity-70" aria-hidden />
    {text}
  </span>
);

export default RuleRangeChoice;
